import { useRef, useState } from "react";
import moment from "moment";
import { ITEMS_PER_PAGE } from "../../constants";

export const useAllInvoiceReport = ({
  business,
  filters,
  type,
  initialInvoices = [],
}) => {
  const isFirstRender = useRef(true);
  const firstRender = isFirstRender.current;

  const initialData =
    firstRender && initialInvoices?.length
      ? initialInvoices.slice(0, ITEMS_PER_PAGE)
      : [];

  const [state, setState] = useState({
    isLoading: true,
    totalInvoiceValue: 0,
    invoiceList: initialData,
    totalPaid: 0,
    totalUnPaid: 0,
    totalOverdue: 0,
    totalOverdueCount: 0,
    totalUnPaidCount: 0,
    totalPaidCount: 0,
    totalInvoiceFilterCount: 0,
    totalInvoiceAmount: 0,
    totalInvoiceCount: 0,
  });

  const getDateRange = (date) => ({
    start: date?.startDate
      ? moment(date.startDate).startOf("day").valueOf()
      : undefined,
    end: date?.endDate
      ? moment(date.endDate).endOf("day").valueOf()
      : undefined,
  });

  const calculateTotals = (data = []) => {
    let totals = {
      totalPaid: 0,
      totalUnPaid: 0,
      totalOverdue: 0,
      totalPaidCount: 0,
      totalUnPaidCount: 0,
      totalOverdueCount: 0,
      totalInvoiceAmount: 0,
      totalInvoiceValue: 0,
      totalFilteredCount: data.length,
    };

    data.forEach((item) => {
      totals.totalInvoiceAmount += item.amount;
      totals.totalInvoiceValue += item.profit || 0;

      if (item.status === "paid") {
        totals.totalPaid += item.amount;
        totals.totalPaidCount++;
      }

      if (
        item.status !== "paid" &&
        item?.due_date &&
        item?.due_date > 0 &&
        item.due_date < moment().startOf("day").valueOf()
      ) {
        totals.totalOverdue += item.amount - Number(item.advance_amount || 0);
        totals.totalOverdueCount++;
      }

      if (item.status !== "paid") {
        totals.totalUnPaid += item.amount - Number(item.advance_amount || 0);
        totals.totalUnPaidCount++;
      }
    });

    return totals;
  };

  const handleInvoice = async (limit, offset, isCalculateTotal = true) => {
    const { date, amount, method, sort, status, keywords } = filters || {};

    setState((prev) => ({ ...prev, isLoading: true }));

    const { start, end } = getDateRange(date);

    try {
      let totals = {};
      let totalInvoiceCount = state.totalInvoiceCount;

      // TOTALS (optimized like quickSale)
      if (isCalculateTotal) {
        let rawData;

        if (firstRender) {
          rawData = initialInvoices;
        } else {
          const baseQuery = business.getInvoiceReport(
            start,
            end,
            keywords || "",
            type,
            status,
            undefined,
            undefined,
            undefined,
            amount,
            method,
          );

          rawData = await baseQuery.unsafeFetchRaw();
        }

        totals = calculateTotals(rawData);

        // ✅ overall count (no filter)
        totalInvoiceCount = await business
          .getInvoiceReport(
            moment("20180101").startOf("day").valueOf(),
            null,
            "",
            type,
            [],
          )
          .fetchCount();
      }

      // ✅ PAGINATION (same pattern as quickSale)
      const paginatedItems = firstRender
        ? initialData
        : await business
            .getInvoiceReport(
              start,
              end,
              keywords || "",
              type,
              status,
              sort || "Newest",
              limit,
              offset,
              amount,
              method,
            )
            .fetch();

      const updatedList =
        offset > 0 ? [...state.invoiceList, ...paginatedItems] : paginatedItems;

      isFirstRender.current = false;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        invoiceList: updatedList,
        ...(isCalculateTotal && {
          totalPaid: totals.totalPaid,
          totalUnPaid: totals.totalUnPaid,
          totalOverdue: totals.totalOverdue,
          totalInvoiceFilterCount: totals.totalFilteredCount,
          totalPaidCount: totals.totalPaidCount,
          totalUnPaidCount: totals.totalUnPaidCount,
          totalOverdueCount: totals.totalOverdueCount,
          totalInvoiceAmount: totals.totalInvoiceAmount,
          totalInvoiceValue: totals.totalInvoiceValue,
          totalInvoiceCount,
        }),
      }));
    } catch (error) {
      console.error(error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  return { ...state, handleInvoice };
};
